import {Injectable} from "@angular/core";
import {BehaviorSubject, combineLatest, Observable} from "rxjs";
import {map} from "rxjs/operators";
import {HttpClient} from "@angular/common/http";
import * as camelcaseKeys from "camelcase-keys";
import Big from "big.js";
import {CombinedCryptoData, MarketData} from "../../../../core/models/market.model";
import {CurrencyDetails} from "../../../../core/models/currency.model";

@Injectable({
  providedIn: 'root'
})
export class MarketDataService {
  private marketDataSubject = new BehaviorSubject<MarketData[]>([]);
  private currencyDataSubject = new BehaviorSubject<CurrencyDetails[]>([]);

  get marketData$() {
    return this.marketDataSubject.asObservable();
  }

  get currencyData$() {
    return this.currencyDataSubject.asObservable();
  }
  constructor(
    private http: HttpClient
  ) {
    this.getAllMarkets().then();
    this.getAllCurrencies().then();
  }

  async getAllCurrencies(): Promise<void> {
    const response: any = await this.http.get('/v1/currencies').toPromise();
    const transformedData = response.data.map((each: MarketData) => camelcaseKeys(each, { deep: true }));
    this.currencyDataSubject.next(transformedData);
  }

  async getAllMarkets(): Promise<void> {
    const response: any = await this.http.get('/v1/market').toPromise();
    const transformedData = response.data.map((each: MarketData) => camelcaseKeys(each, { deep: true }));
    this.marketDataSubject.next(transformedData);
  }

  getCombinedMarketCurrencyData(baseMarket: 'IRT' | 'USDT' = 'IRT'): Observable<CombinedCryptoData[]> {
    return combineLatest([this.currencyData$, this.marketData$]).pipe(
      map(([currencies, markets]) => {
        const combinedData: CombinedCryptoData[] = [];
        for (const currency of currencies) {
          const correspondingMarket = markets.find(market => market.baseCurrency.id === currency.id && market.quoteCurrency.id === (baseMarket === 'IRT' ? 'IRR' : 'USDT' ));

          if (correspondingMarket) {
            const combined: CombinedCryptoData = {
              id: currency.id,
              name: correspondingMarket.name,
              price24hChange: this.calculate24HourPriceChangePercentage(correspondingMarket.history),
              baseCurrencyId: correspondingMarket.baseCurrency.id,
              quoteCurrencyId: baseMarket,
              lastPrice: correspondingMarket.quoteCurrency.id === 'IRR' ? new Big(correspondingMarket.lastPrice).div(10).toNumber() : +correspondingMarket.lastPrice,
              quoteCurrencyPrecision: correspondingMarket.quoteCurrencyPrecision,
              volume: correspondingMarket.quoteCurrency.id === 'IRR' ? new Big(correspondingMarket.lastVolume).div(10).toNumber() : +correspondingMarket.lastVolume,
              category: currency.category,
            } as unknown as CombinedCryptoData

            combinedData.push(combined);
          }
        }
        return combinedData;
      })
    )
  }

  private calculate24HourPriceChangePercentage(prices: string[]): string {
    if (prices.length < 1) {
      return 'NaN'
    }

    const firstPrice = new Big(prices[0]);
    const lastPrice = new Big(prices[prices.length - 1]);

    const priceChange = lastPrice.minus(firstPrice);
    const priceChangePercentage = priceChange.div(firstPrice).times(100);

    return priceChangePercentage.toFixed(2);
  }
}
